import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, ExternalLink, FolderInput, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { api, type Doc, type Section } from '../lib/api';
import { NoAccess } from './Login';
import { docIcon } from '../lib/doc-icons';
import {
  R, btnAccent, btnOutline, chip, ft, label, mono, plural,
} from '../lib/ui';
import { Label } from '../components/Primitives';
import { useContextMenu, type MenuEntry } from '../components/ContextMenu';
import { useDialog } from '../components/DialogHost';
import { useToast } from '../components/Toast';
import { DocPropertiesModal } from '../components/DocPropertiesModal';

/** Раскладка колонок таблицы: иконка, название, владелец, доступ, дата, публикация, меню. */
const COLUMNS = '26px minmax(200px, 1fr) 160px 140px 110px 120px 32px';

export function DocList() {
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const dialog = useDialog();
  const toast = useToast();
  const menu = useContextMenu();

  const [filter, setFilter] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const me = useQuery({ queryKey: ['me'], queryFn: api.me });
  const docsQuery = useQuery({ queryKey: ['docs'], queryFn: api.docs });
  const sectionsQuery = useQuery({ queryKey: ['sections'], queryFn: api.sections });

  const docs = docsQuery.data ?? [];
  const sections = sectionsQuery.data ?? [];
  const canEdit = me.data?.role === 'admin' || me.data?.role === 'editor';
  const isAdmin = me.data?.role === 'admin';

  // Если у обычного сотрудника (не админа) есть ровно 1 доступный документ — сразу открываем его
  useEffect(() => {
    if (!canEdit && docs.length === 1 && !docsQuery.isLoading) {
      navigate(`/d/${docs[0].id}`, { replace: true });
    }
  }, [canEdit, docs, docsQuery.isLoading, navigate]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['docs'] });
    qc.invalidateQueries({ queryKey: ['sections'] });
  };

  const createSection = useMutation({
    mutationFn: (name: string) => api.sectionCreate(name),
    onSuccess: invalidate,
  });

  const renameSection = useMutation({
    mutationFn: (v: { id: string; name: string }) => api.sectionUpdate(v.id, { name: v.name }),
    onSuccess: invalidate,
  });

  const moveSection = useMutation({
    mutationFn: (v: { id: string; position: number }) =>
      api.sectionUpdate(v.id, { position: v.position }),
    onSuccess: invalidate,
  });

  const removeSection = useMutation({
    mutationFn: (id: string) => api.sectionDelete(id),
    onSuccess: invalidate,
  });

  const createDoc = useMutation({
    mutationFn: (sectionId?: string) => api.createDoc('Новый документ', undefined, sectionId),
    onSuccess: (doc) => {
      invalidate();
      navigate(`/d/${doc.id}`);
    },
  });

  const removeDoc = useMutation({
    mutationFn: (id: string) => api.deleteDoc(id),
    onSuccess: invalidate,
  });

  const moveDoc = useMutation({
    mutationFn: (v: { docId: string; sectionId: string }) =>
      api.updateDoc(v.docId, { sectionId: v.sectionId }),
    onSuccess: invalidate,
  });

  const duplicateDoc = useMutation({
    mutationFn: (v: { docId: string; name?: string }) => api.duplicateDoc(v.docId, { name: v.name }),
    onSuccess: (newDoc) => {
      invalidate();
      toast(`Создана копия «${newDoc.name}»`, 'ok');
    },
    onError: (e) => toast(`Ошибка копирования: ${(e as Error).message}`, 'err'),
  });

  const askName = async (title: string, value = '') => {
    const answer = await dialog.prompt({ title, placeholder: 'Например, Отчёты', value });
    return answer?.trim() ?? '';
  };

  const groups = useMemo(
    () =>
      (filter === null ? sections : sections.filter((s) => s.id === filter)).map((section) => ({
        section,
        docs: docs.filter((d) => d.sectionId === section.id),
      })),
    [sections, docs, filter],
  );

  const shown = groups.reduce((sum, g) => sum + g.docs.length, 0);

  const docMenu = (doc: Doc): MenuEntry[] => [
    { type: 'header', label: doc.name },
    { label: 'Открыть', icon: ExternalLink, onClick: () => navigate(`/d/${doc.id}`) },
    ...(canEdit
      ? [
          {
            label: 'Создать копию',
            icon: Copy,
            onClick: async () => {
              const customName = await dialog.prompt({
                title: 'Создать копию таблицы',
                body: 'Название для новой копии таблицы:',
                value: `${doc.name} (копия)`,
              });
              if (customName !== null) {
                duplicateDoc.mutate({ docId: doc.id, name: customName.trim() || undefined });
              }
            },
          } as MenuEntry,
          { label: 'Изменить', icon: Pencil, onClick: () => setEditing(doc.id) } as MenuEntry,
        ]
      : []),
    ...(canEdit && sections.length > 1
      ? [
          {
            label: 'Переместить в раздел',
            icon: FolderInput,
            submenu: sections.map((s) => ({
              label: s.name,
              disabled: s.id === doc.sectionId,
              onClick: () => moveDoc.mutate({ docId: doc.id, sectionId: s.id }),
            })),
          } as MenuEntry,
        ]
      : []),
    ...(isAdmin
      ? [
          { type: 'divider' } as MenuEntry,
          {
            label: 'Удалить',
            icon: Trash2,
            danger: true,
            onClick: async () => {
              const ok = await dialog.confirm({
                title: 'Удалить документ?',
                body: `«${doc.name}» будет удалён вместе со всеми таблицами. Восстановить нельзя.`,
                danger: true,
              });
              if (ok) removeDoc.mutate(doc.id);
            },
          } as MenuEntry,
        ]
      : []),
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        background: R.bg,
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 16,
          padding: '24px 28px 16px',
          borderBottom: ft.strong,
          flex: '0 0 auto',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Label>Организация · GTA5RP</Label>
          <h2 style={{ margin: '2px 0 0', fontSize: 30, color: R.text }}>Все документы</h2>
        </div>

        <span
          style={{ fontSize: 12, fontFamily: mono, color: R.textMuted, paddingBottom: 6 }}
        >
          {docsQuery.isLoading
            ? 'Загрузка…'
            : `${docs.length} ${plural(docs.length, 'таблица', 'таблицы', 'таблиц')} в ` +
              `${sections.length} ${plural(sections.length, 'разделе', 'разделах', 'разделах')}`}
        </span>

        {canEdit && (
          <div style={{ marginLeft: 'auto', alignSelf: 'center', display: 'flex', gap: 10 }}>
            <button
              style={btnOutline}
              onClick={async () => {
                const name = await askName('Новый раздел');
                if (name) createSection.mutate(name);
              }}
            >
              Новый раздел
            </button>
            <button style={btnAccent} onClick={() => createDoc.mutate(undefined)}>
              Новая таблица
            </button>
          </div>
        )}
      </header>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          minHeight: 44,
          padding: '0 28px',
          borderBottom: ft.hair,
          flexWrap: 'wrap',
        }}
      >
        <button style={chip(filter === null)} onClick={() => setFilter(null)}>
          Все разделы
        </button>

        {sections.map((s) => (
          <button
            key={s.id}
            style={chip(filter === s.id)}
            onClick={() => setFilter(filter === s.id ? null : s.id)}
          >
            {s.name}
            <span style={{ fontFamily: mono, opacity: 0.7 }}>{s.docCount}</span>
          </button>
        ))}

        {canEdit && (
          <button
            style={{ ...chip(false), borderStyle: 'dashed', color: R.textMuted }}
            onClick={async () => {
              const name = await askName('Новый раздел');
              if (name) createSection.mutate(name);
            }}
          >
            + раздел
          </button>
        )}

        <span style={{ marginLeft: 'auto', fontSize: 12, color: R.textMuted }}>
          Показано {shown} из {docs.length}
        </span>
      </div>

      {docs.length === 0 && !docsQuery.isLoading ? (
        !canEdit ? (
          <NoAccess />
        ) : (
          <EmptyState canEdit={!!canEdit} onCreate={() => createDoc.mutate(undefined)} />
        )
      ) : (
        <div className="rt-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: COLUMNS,
              height: 36,
              padding: '0 28px',
              alignItems: 'center',
              background: R.bgSubtle,
              borderBottom: ft.strong,
              ...label,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <span />
            <span>Название</span>
            <span>Владелец</span>
            <span>Доступ</span>
            <span>Изменён</span>
            <span>Публикация</span>
            <span />
          </div>

          {groups.map(({ section, docs: list }, index) => (
            <div key={section.id}>
              <SectionRow
                section={section}
                count={list.length}
                canEdit={!!canEdit}
                index={index}
                total={groups.length}
                onCreateDoc={() => createDoc.mutate(section.id)}
                onRename={async () => {
                  const name = await askName('Переименовать раздел', section.name);
                  if (name && name !== section.name) {
                    renameSection.mutate({ id: section.id, name });
                  }
                }}
                onMove={(delta) => {
                  const neighbour = groups[index + delta]?.section;
                  if (!neighbour) return;
                  moveSection.mutate({ id: section.id, position: neighbour.position });
                  moveSection.mutate({ id: neighbour.id, position: section.position });
                }}
                onDelete={async () => {
                  const ok = await dialog.confirm({
                    title: 'Удалить раздел?',
                    body: list.length
                      ? `Раздел «${section.name}» будет удалён, а ${list.length} ` +
                        `${plural(list.length, 'документ', 'документа', 'документов')} ` +
                        'переедут в «Без раздела».'
                      : `Раздел «${section.name}» будет удалён.`,
                    danger: true,
                  });
                  if (ok) removeSection.mutate(section.id);
                }}
              />

              {list.length === 0 && (
                <div
                  style={{
                    padding: '14px 28px',
                    fontSize: 12.5,
                    color: R.textMuted,
                    borderBottom: ft.hair,
                  }}
                >
                  В разделе пока нет документов
                </div>
              )}

              {list.map((doc) => (
                <DocRow
                  key={doc.id}
                  doc={doc}
                  onOpen={() => navigate(`/d/${doc.id}`)}
                  onMenu={(e) => menu.open(e, docMenu(doc))}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      <menu.Portal />

      {editing && (
        <DocPropertiesModal
          doc={docs.find((d) => d.id === editing)!}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/** Кнопка-ссылка в строке раздела: без рамки, подсвечивается акцентом. */
function InlineAction({
  children, onClick, disabled, danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  const base = disabled ? R.textMuted : danger ? R.danger : R.textMuted;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontSize: 11.5,
        padding: 0,
        color: base,
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'default' : 'pointer',
        background: 'transparent',
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.color = R.accentText;
      }}
      onMouseLeave={(e) => (e.currentTarget.style.color = base)}
    >
      {children}
    </button>
  );
}

function SectionRow({
  section, count, canEdit, index, total, onRename, onMove, onDelete, onCreateDoc,
}: {
  section: Section;
  count: number;
  canEdit: boolean;
  index: number;
  total: number;
  onRename: () => void;
  onMove: (delta: number) => void;
  onDelete: () => void;
  onCreateDoc: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minHeight: 34,
        padding: '6px 28px',
        background: R.selectionSubtle,
        borderBottom: ft.hair,
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: R.text,
        }}
      >
        {section.name}
      </span>
      <span style={{ fontSize: 11, fontFamily: mono, color: R.textMuted }}>{count}</span>

      {canEdit && (
        <span
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            fontSize: 11.5,
          }}
        >
          <InlineAction onClick={onCreateDoc}>+ документ</InlineAction>
          <InlineAction onClick={onRename}>переименовать</InlineAction>
          <InlineAction onClick={() => onMove(-1)} disabled={index <= 0}>
            ↑
          </InlineAction>
          <InlineAction onClick={() => onMove(1)} disabled={index >= total - 1}>
            ↓
          </InlineAction>
          <InlineAction onClick={onDelete} danger>
            удалить
          </InlineAction>
        </span>
      )}
    </div>
  );
}

function DocRow({
  doc, onOpen, onMenu,
}: {
  doc: Doc;
  onOpen: () => void;
  onMenu: (e: React.MouseEvent) => void;
}) {
  const Icon = docIcon(doc.icon);
  const access = doc.published
    ? 'Публичный'
    : doc.shareLinks
      ? `По ссылке · ${doc.shareLinks}`
      : 'Команда';

  return (
    <div
      onClick={onOpen}
      onContextMenu={onMenu}
      style={{
        display: 'grid',
        gridTemplateColumns: COLUMNS,
        minHeight: 58,
        padding: '0 28px',
        alignItems: 'center',
        borderBottom: ft.hair,
        fontSize: 13.5,
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = R.selectionSubtle)}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <span
        style={{
          width: 26,
          height: 26,
          background: R.accentSubtle,
          border: ft.edge,
          display: 'grid',
          placeItems: 'center',
          color: R.accent,
        }}
      >
        <Icon size={14} />
      </span>

      <span
        style={{
          display: 'flex',
          flexDirection: 'column',
          lineHeight: 1.3,
          minWidth: 0,
          paddingRight: 16,
        }}
      >
        <span
          style={{
            fontWeight: 600,
            color: R.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {doc.name}
        </span>
        <span style={{ fontSize: 11, fontFamily: mono, color: R.textMuted }}>{doc.id}</span>
      </span>

      <span style={{ color: R.textMuted, fontSize: 12.5 }}>—</span>
      <span style={{ color: R.textMuted, fontSize: 12.5 }}>{access}</span>
      <span
        style={{ color: R.textMuted, fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}
      >
        {new Date(doc.updatedAt).toLocaleDateString('ru')}
      </span>

      <span
        style={{
          justifySelf: 'start',
          display: 'inline-flex',
          alignItems: 'center',
          padding: '2px 7px',
          fontSize: 11.5,
          whiteSpace: 'nowrap',
          border: `1px solid ${doc.published ? R.accentBorder : R.grid}`,
          color: doc.published ? R.accent : R.textMuted,
          background: doc.published ? R.accentSubtle : 'transparent',
        }}
      >
        {doc.published ? 'Опубликован' : 'Черновик'}
      </span>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onMenu(e);
        }}
        style={{
          display: 'grid',
          placeItems: 'center',
          width: 26,
          height: 26,
          color: R.textMuted,
          background: 'transparent',
        }}
      >
        <MoreVertical size={15} />
      </button>
    </div>
  );
}

function EmptyState({ canEdit, onCreate }: { canEdit: boolean; onCreate: () => void }) {
  const Icon = docIcon('clipboard');

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '40px 28px',
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          background: R.accentSubtle,
          border: ft.edge,
          display: 'grid',
          placeItems: 'center',
          color: R.accent,
          marginBottom: 20,
        }}
      >
        <Icon size={30} />
      </div>

      <h2 style={{ fontSize: 26, color: R.text, marginBottom: 8 }}>Пока нет документов</h2>
      <p style={{ fontSize: 13, color: R.textMuted, maxWidth: 460, lineHeight: 1.5 }}>
        Создайте первую таблицу — она появится в списке и станет доступна всем,
        у кого есть доступ к разделу.
      </p>

      {canEdit && (
        <button style={{ ...btnAccent, marginTop: 20 }} onClick={onCreate}>
          Новая таблица
        </button>
      )}
    </div>
  );
}
